import {animateByClass} from '../../Library/Animation';

const initWizard         = (wizard, options = {}) => {
    const panels         = Array.from(wizard.querySelectorAll('.wizard-panel'));
    const stepIndicators = Array.from(wizard.querySelectorAll('.wizard-steps .step'));

    const backBtn      = wizard.querySelector('[data-wizard-action="back"]');
    const continueBtn  = wizard.querySelector('[data-wizard-action="continue"]');
    const submitBtn    = wizard.querySelector('[data-wizard-submit]');
    const cancelBtn    = wizard.querySelector('[data-wizard-cancel]');
    const extraButtons = wizard.querySelectorAll('[data-wizard-extra]');

    let currentStep = 0;
    let isAnimating = false;

    const validate    = options.validate || (() => true);
    const onStepEnter = options.onStepEnter || (() => {});

    const updateFooterButtons = () => {
        backBtn?.classList.toggle('hidden', currentStep === 0);
        continueBtn?.classList.toggle('hidden', currentStep === panels.length - 1);
        submitBtn?.classList.toggle('hidden', currentStep !== panels.length - 1);
        cancelBtn?.classList.toggle('hidden', currentStep !== 0);
        extraButtons.forEach(button => button.classList.toggle('hidden', currentStep === panels.length - 1));
    };

    const showStepImmediate = targetIndex => {
        panels.forEach((panel, index) => {
            panel.classList.toggle('hidden', index !== targetIndex);
        });

        currentStep = targetIndex;
        updateStepIndicators();
        updateFooterButtons();
        onStepEnter(panels[targetIndex], targetIndex);
    };

    const updateStepIndicators = () => {
        stepIndicators.forEach((step, index) => {
            step.classList.remove('active', 'completed');

            if (index < currentStep) {
                step.classList.add('completed');
            } else if (index === currentStep) {
                step.classList.add('active');
            }
        });
    };

    const goToStep = async targetIndex => {
        if (isAnimating || targetIndex === currentStep) {
            return;
        }

        if (targetIndex < 0 || targetIndex >= panels.length) {
            return;
        }

        isAnimating = true;

        const forward      = targetIndex > currentStep;
        const currentPanel = panels[currentStep];
        const targetPanel  = panels[targetIndex];

        wizard.dispatchEvent(new CustomEvent('wizard-step-changed', {
            bubbles: true,
            detail: {fromPanel: currentPanel, toPanel: targetPanel},
        }));

        currentStep = targetIndex;
        updateStepIndicators();
        updateFooterButtons();

        targetPanel.classList.remove('hidden');

        await Promise.all([
            animateByClass(currentPanel, forward ? 'slide-out-left' : 'slide-out-right'),
            animateByClass(targetPanel, forward ? 'slide-in-right' : 'slide-in-left'),
        ]);

        currentPanel.classList.add('hidden');
        onStepEnter(panels[targetIndex], targetIndex);

        isAnimating = false;
    };

    const isLastStep = () => currentStep === panels.length - 1;

    const getCurrentStep = () => currentStep;

    const getCurrentPanel = () => panels[currentStep];

    wizard.addEventListener('click', event => {
        const button = event.target.closest('[data-wizard-action]');

        if (!button) {
            return;
        }

        const action = button.dataset.wizardAction;

        if (action === 'continue' && validate(panels[currentStep], currentStep)) {
            goToStep(currentStep + 1);
        }

        if (action === 'back') {
            goToStep(currentStep - 1);
        }
    });

    stepIndicators.forEach(step => {
        step.addEventListener('click', () => {
            const targetIndex = parseInt(step.dataset.stepIndex, 10);

            if (targetIndex < currentStep) {
                goToStep(targetIndex);
            }
        });
    });

    wizard.addEventListener('keydown', event => {
        if (event.key !== 'Enter') {
            return;
        }

        if (event.target.closest('button, a')) {
            return;
        }

        const fieldExemptSelector = ['.select-container', wizard.dataset.enterIgnore]
            .filter(Boolean)
            .join(', ')
        ;

        if (event.target.closest(fieldExemptSelector)) {
            event.preventDefault();

            return;
        }

        event.preventDefault();

        if (isLastStep()) {
            const markedTrigger = wizard.querySelector('[data-wizard-submit-trigger]');
            const submitTrigger = markedTrigger ?? (submitBtn?.matches('button, a') ? submitBtn : null);

            submitTrigger?.click();

            return;
        }

        if (validate(panels[currentStep], currentStep)) {
            goToStep(currentStep + 1);
        }
    });

    const initialStepName = wizard.dataset.initialStep;

    if (initialStepName) {
        const initialIndex = panels.findIndex(panel => panel.dataset.wizardStep === initialStepName);

        if (initialIndex > 0) {
            showStepImmediate(initialIndex);
        }
    }

    return {goToStep, getCurrentStep, getCurrentPanel, isLastStep};
};

export {initWizard};
